<template>
  <div class="map-container">

    <div ref="mapContainer" class="map"></div>

    <!-- Zoom controls - top right corner -->
    <div class="zoom-controls" role="group" aria-label="Map zoom controls">
      <v-btn
        icon
        size="small"
        class="zoom-btn"
        @click="zoomIn"
        title="Zoom In"
        aria-label="Zoom in"
      >
        <v-icon>mdi-plus</v-icon>
      </v-btn>
      <v-btn
        icon
        size="small"
        class="zoom-btn"
        @click="zoomOut"
        title="Zoom Out"
        aria-label="Zoom out"
      >
        <v-icon>mdi-minus</v-icon>
      </v-btn>
      <v-btn
        icon
        size="small"
        class="zoom-btn"
        @click="resetView"
        title="Reset View"
        aria-label="Reset map view to default position"
      >
        <v-icon>mdi-crosshairs-gps</v-icon>
      </v-btn>
    </div>

    <!-- SVG line connecting tooltip to marker -->
    <svg
      v-if="selectedDataCenter && connectorLine.visible"
      class="tooltip-connector-line"
      :style="{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 999
      }"
    >
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      <line
        :x1="connectorLine.x1"
        :y1="connectorLine.y1"
        :x2="connectorLine.x2"
        :y2="connectorLine.y2"
        stroke="#CCCCCC"
        stroke-width="2"
        stroke-dasharray="5,5"
        opacity="0.8"
      />
      <!-- Small circle at marker position -->
      <circle
        :cx="connectorLine.x2"
        :cy="connectorLine.y2"
        r="6"
        fill="none"
        stroke="#CCCCCC"
        stroke-width="2"
        opacity="0.8"
      />
    </svg>

    <v-card
      v-if="selectedDataCenter"
      class="tooltip-card"
      :style="tooltipStyle"
    >
      <v-card-title class="pb-2">
        {{ selectedDataCenter.name }}
      </v-card-title>
      <v-card-text>
        <div><strong>Location:</strong> {{ selectedDataCenter.location || 'N/A' }}</div>
        <div><strong>Company:</strong> {{ selectedDataCenter.company_name || 'N/A' }}</div>
        <div><strong>Power Capacity:</strong> {{ formatPowerCapacity(selectedDataCenter) }}</div>
        <div><strong>Building Area:</strong> {{ formatBuildingArea(selectedDataCenter) }}</div>
        <div><strong>Facility Age:</strong> {{ formatFacilityAge(selectedDataCenter) }}</div>
        <div><strong>Ownership:</strong> {{ formatCapitalInvestment(selectedDataCenter) }}</div>
      </v-card-text>
      <v-divider style="border-color: #e0e0e0;" />
      <v-card-text class="pa-2" v-if="selectedDataCenter && selectedDataCenter.estimated_capacity">
        <div class="text-caption text-center" style="color: #666666; font-size: 0.7rem;">
          * Estimated capacity
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import Supercluster from 'supercluster'

// Lazy load heavy mapping dependencies
let mapboxgl = null
let Deck = null
let IconLayer = null
let MapboxOverlay = null

const loadMappingLibraries = async () => {
  if (mapboxgl) return // Already loaded
  
  const [
    mapboxModule,
    deckModule,
    layersModule,
    mapboxOverlayModule
  ] = await Promise.all([
    import('mapbox-gl'),
    import('@deck.gl/core'),
    import('@deck.gl/layers'),
    import('@deck.gl/mapbox')
  ])
  
  mapboxgl = mapboxModule.default
  Deck = deckModule.Deck
  IconLayer = layersModule.IconLayer
  MapboxOverlay = mapboxOverlayModule.MapboxOverlay
}

export default {
  name: 'MapComponent',
  props: {
    dataCenters: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    mapStyle: {
      type: String,
      default: 'dark'
    },
    colorBy: {
      type: String,
      default: 'status' // 'status' or 'capital'
    }
  },
  emits: ['data-center-select'],
  setup(props, { emit }) {
    const mapContainer = ref(null)
    const selectedDataCenter = ref(null)
    const tooltipStyle = ref({})
    const currentMapStyle = ref('dark')
    const connectorLine = ref({
      visible: false,
      x1: 0,
      y1: 0,
      x2: 0,
      y2: 0
    })
    // Watch for mapStyle prop changes
    watch(() => props.mapStyle, (newStyle) => {
      if (map && mapStyles[newStyle]) {
        changeMapStyle(newStyle)
      }
    })
    
    let map = null
    let deckOverlay = null
    let hoveredObject = null
    let iconAtlas = null
    let iconMapping = null
    let supercluster = null
    let lastUpdateTimestamp = 0
    let clusterLabelElements = [] // Store HTML label elements

    // Map configuration - single source of truth for default view and animations
    const MAP_CONFIG = {
      defaultCenter: [103.76, 1.49],  // Johor Bahru region, Malaysia
      defaultZoom: 9,
      animation: {
        zoom: 300,      // Quick feedback for zoom buttons
        reset: 1000,    // Smooth transition for reset
        flyTo: 2000     // Dramatic navigation for location jumps
      }
    }

    // Define flyToLocation method that can be called from parent
    const flyToLocation = (longitude, latitude, zoom = 16, datacenter = null) => {
      if (map && longitude !== undefined && latitude !== undefined) {
        // Hide any existing tooltip and connector line before starting new navigation
        selectedDataCenter.value = null;
        connectorLine.value.visible = false;

        // Set up one-time listener for when flyTo animation completes
        const showTooltipAfterFly = () => {
          if (datacenter) {
            // Set the selected datacenter to show tooltip
            selectedDataCenter.value = datacenter;

            // Position tooltip at center-right of the screen for visibility
            const mapWidth = mapContainer.value?.offsetWidth || window.innerWidth;
            const mapHeight = mapContainer.value?.offsetHeight || window.innerHeight;

            const tooltipLeft = mapWidth / 2 + 100;
            const tooltipTop = mapHeight / 2 - 150;

            tooltipStyle.value = {
              left: `${tooltipLeft}px`, // Center-right position
              top: `${tooltipTop}px`, // Slightly above center
              position: 'fixed',
              zIndex: 1000
            };

            // Calculate marker position on screen using Mapbox project method
            const markerPoint = map.project([longitude, latitude]);

            // Calculate line coordinates from tooltip to marker
            // Tooltip left edge is the start point
            connectorLine.value = {
              visible: true,
              x1: tooltipLeft, // Left edge of tooltip
              y1: tooltipTop + 100, // Middle of tooltip (approximate)
              x2: markerPoint.x, // Marker X position
              y2: markerPoint.y // Marker Y position
            };
          }
        };

        // Listen for moveend event which fires when flyTo completes
        map.once('moveend', showTooltipAfterFly);

        map.flyTo({
          center: [longitude, latitude],
          zoom: zoom,
          duration: MAP_CONFIG.animation.flyTo,
          essential: true
        });
      }
    }

    // Zoom control methods
    const zoomIn = () => {
      map?.zoomIn({ duration: MAP_CONFIG.animation.zoom })
    }

    const zoomOut = () => {
      map?.zoomOut({ duration: MAP_CONFIG.animation.zoom })
    }

    const resetView = () => {
      if (!map) return

      map.flyTo({
        center: MAP_CONFIG.defaultCenter,
        zoom: MAP_CONFIG.defaultZoom,
        duration: MAP_CONFIG.animation.reset,
        essential: true
      })
      // Clear any selected datacenter tooltip
      selectedDataCenter.value = null
      connectorLine.value.visible = false
    }

    let isUpdating = false
    let isStyleChanging = false // Flag to prevent updates during style changes
    let currentLayers = []
    // HTML markers removed - using unified Deck.GL approach

    const getStatusColor = (status) => {
      switch (status) {
        case 'operational': return 'success'
        case 'maintenance': return 'warning'
        case 'offline': return 'error'
        default: return 'grey'
      }
    }

    const getMarkerColor = (status) => {
      switch (status) {
        case 'operational': return '#4CAF50' // Green
        case 'maintenance': return '#FF9800' // Orange  
        case 'offline': return '#F44336' // Red
        default: return '#9E9E9E' // Grey
      }
    }

    // Get capital ownership category color
    const getCapitalColor = (chCapitalEn) => {
      if (!chCapitalEn || chCapitalEn === '') return '#9E9E9E' // Grey - No data

      const ownership = chCapitalEn.toLowerCase()

      if (ownership.includes('non-chinese')) return '#2196F3' // Blue - Non-Chinese Capital
      if (ownership.includes('chinese')) return '#FF8A80' // Light red - Chinese Capital
      if (ownership.includes('joint')) return '#FFFFFF' // White - Joint Venture

      return '#9E9E9E' // Grey - Unknown
    }

    // Get capital ownership category name
    const getCapitalCategory = (chCapitalEn) => {
      if (!chCapitalEn || chCapitalEn === '') return 'No Data'
      return chCapitalEn
    }

    // Create cluster-friendly icon atlas
    const createClusterIconAtlas = () => {
      const canvas = document.createElement('canvas')
      const size = 48
      
      // Create a grid for different icons: individual + cluster counts
      const gridWidth = 10 // 10 columns for various cluster sizes
      const gridHeight = 5 // 5 rows for different types
      canvas.width = size * gridWidth
      canvas.height = size * gridHeight
      const ctx = canvas.getContext('2d')
      
      // Clear background
      ctx.fillStyle = 'transparent'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      
      const drawIcon = (x, y, color, text = null, textColor = '#ffffff') => {
        const centerX = x + size / 2
        const centerY = y + size / 2
        const radius = size / 3
        
        // Draw circle
        ctx.fillStyle = color
        ctx.beginPath()
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI)
        ctx.fill()
        
        // Add border
        ctx.strokeStyle = '#ffffff'
        ctx.lineWidth = 2
        ctx.stroke()
        
        // Add text for clusters
        if (text) {
          ctx.fillStyle = textColor
          ctx.font = 'bold 12px Arial'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(text, centerX, centerY)
        }
      }
      
      // Row 0: Individual datacenter status icons
      drawIcon(0, 0, '#4CAF50') // operational
      drawIcon(size, 0, '#FF9800') // maintenance  
      drawIcon(size * 2, 0, '#F44336') // offline
      drawIcon(size * 3, 0, '#9E9E9E') // default
      
      // Row 1: Small clusters (2-9)
      for (let i = 2; i <= 9; i++) {
        drawIcon(size * (i - 2), size, '#2196F3', i.toString())
      }
      
      // Row 2: Medium clusters (10-90, by 10s)
      for (let i = 1; i <= 9; i++) {
        drawIcon(size * (i - 1), size * 2, '#1976D2', (i * 10).toString())
      }
      
      // Row 3: Large clusters (100+)
      const largeCounts = [100, 200, 300, 400, 500, 600, 700, 800, 900]
      largeCounts.forEach((count, i) => {
        drawIcon(size * i, size * 3, '#0D47A1', count >= 100 ? `${Math.floor(count/100)}+` : count.toString())
      })
      
      return canvas.toDataURL()
    }

    // Convert color to RGB array for Deck.gl fallback
    const getMarkerColorRGB = (status) => {
      switch (status) {
        case 'operational': return [76, 175, 80] // Green
        case 'maintenance': return [255, 152, 0] // Orange  
        case 'offline': return [244, 67, 54] // Red
        default: return [158, 158, 158] // Grey
      }
    }

    // Convert capital ownership category to RGB array for Deck.gl
    const getCapitalColorRGB = (chCapitalEn) => {
      if (!chCapitalEn || chCapitalEn === '') return [128, 128, 128] // Grey - No data

      // Use ownership categories from ch_capital_en
      const ownership = chCapitalEn.toLowerCase()

      if (ownership.includes('non-chinese')) return [255, 255, 255] // White - Non-Chinese Capital
      if (ownership.includes('chinese')) return [255, 0, 0] // Red - Chinese Capital
      if (ownership.includes('joint')) return [128, 128, 128] // Grey - Joint Venture

      return [128, 128, 128] // Grey - Unknown
    }

    // Minimalist black and white colors for markers
    const getTronMarkerColorRGB = (status) => {
      switch (status) {
        case 'operational': return [0, 0, 0, 255] // Black
        case 'maintenance': return [128, 128, 128, 255] // Gray
        case 'offline': return [200, 200, 200, 255] // Light gray
        default: return [0, 0, 0, 255] // Black
      }
    }

    // Minimalist colors for capital ownership categories
    const getTronCapitalColorRGB = (chCapitalEn) => {
      if (!chCapitalEn || chCapitalEn === '') return [128, 128, 128, 255] // Grey - No data

      // Use ownership categories from ch_capital_en
      const ownership = chCapitalEn.toLowerCase()

      if (ownership.includes('non-chinese')) return [255, 255, 255, 255] // White - Non-Chinese Capital
      if (ownership.includes('chinese')) return [255, 0, 0, 255] // Red - Chinese Capital
      if (ownership.includes('joint')) return [128, 128, 128, 255] // Grey - Joint Venture

      return [255, 255, 255, 255] // White - Unknown
    }

    const createClusterIconMapping = () => {
      const size = 48
      const mapping = {}
      
      // Individual datacenter status icons (row 0)
      mapping.operational = { x: 0, y: 0, width: size, height: size, anchorX: size / 2, anchorY: size / 2 }
      mapping.maintenance = { x: size, y: 0, width: size, height: size, anchorX: size / 2, anchorY: size / 2 }
      mapping.offline = { x: size * 2, y: 0, width: size, height: size, anchorX: size / 2, anchorY: size / 2 }
      mapping.default = { x: size * 3, y: 0, width: size, height: size, anchorX: size / 2, anchorY: size / 2 }
      
      // Small clusters 2-9 (row 1)
      for (let i = 2; i <= 9; i++) {
        mapping[`cluster-${i}`] = { 
          x: size * (i - 2), y: size, width: size, height: size, anchorX: size / 2, anchorY: size / 2 
        }
      }
      
      // Medium clusters 10-90 (row 2)
      for (let i = 1; i <= 9; i++) {
        mapping[`cluster-${i * 10}`] = { 
          x: size * (i - 1), y: size * 2, width: size, height: size, anchorX: size / 2, anchorY: size / 2 
        }
      }
      
      // Large clusters 100+ (row 3)
      const largeCounts = [100, 200, 300, 400, 500, 600, 700, 800, 900]
      largeCounts.forEach((count, i) => {
        mapping[`cluster-${count}`] = { 
          x: size * i, y: size * 3, width: size, height: size, anchorX: size / 2, anchorY: size / 2 
        }
      })
      
      return mapping
    }

    // Get appropriate icon name for cluster size
    const getClusterIconName = (pointCount) => {
      if (pointCount === 1) return 'operational' // Single points use status-based icons
      if (pointCount < 10) return `cluster-${pointCount}` // 2-9
      if (pointCount < 100) return `cluster-${Math.floor(pointCount / 10) * 10}` // 10,20,30...90
      // For 100+, map to available large cluster icons
      if (pointCount >= 900) return 'cluster-900'
      if (pointCount >= 800) return 'cluster-800' 
      if (pointCount >= 700) return 'cluster-700'
      if (pointCount >= 600) return 'cluster-600'
      if (pointCount >= 500) return 'cluster-500'
      if (pointCount >= 400) return 'cluster-400'
      if (pointCount >= 300) return 'cluster-300'
      if (pointCount >= 200) return 'cluster-200'
      return 'cluster-100' // 100-199
    }

    // Handle duplicate coordinates by slightly offsetting them
    const handleDuplicateCoordinates = (datacenters) => {
      const coordinateMap = new Map()
      const processedDataCenters = []
      
      datacenters.forEach(dc => {
        const coordKey = `${dc.coordinates[0]},${dc.coordinates[1]}`
        
        if (coordinateMap.has(coordKey)) {
          // Found duplicate coordinates - offset this one
          const count = coordinateMap.get(coordKey)
          coordinateMap.set(coordKey, count + 1)
          
          // Create a small circular offset around the original point
          const angle = (count * (360 / 8)) * (Math.PI / 180) // Distribute in circle, max 8 points
          const offsetDistance = 0.0002 // ~20 meters offset
          const offsetLng = offsetDistance * Math.cos(angle)
          const offsetLat = offsetDistance * Math.sin(angle)
          
          processedDataCenters.push({
            ...dc,
            coordinates: [
              dc.coordinates[0] + offsetLng,
              dc.coordinates[1] + offsetLat
            ],
            originalCoordinates: dc.coordinates,
            offsetIndex: count
          })
          
        } else {
          // First occurrence of these coordinates
          coordinateMap.set(coordKey, 1)
          processedDataCenters.push({
            ...dc,
            originalCoordinates: dc.coordinates,
            offsetIndex: 0
          })
        }
      })
      
      return processedDataCenters
    }

    // Initialize clustering
    const initializeClustering = (datacenters) => {
      if (!supercluster) {
        supercluster = new Supercluster({
          radius: 80, // Increased cluster radius for better grouping
          maxZoom: 14, // Increased max zoom to show clusters longer
          minZoom: 0,
          minPoints: 2, // Require at least 2 points to form a cluster
          extent: 512, // Default tile extent
          nodeSize: 64, // Size of KD-tree leaf nodes
          log: false // Disable logging for performance
        })
      }
      
      // Convert datacenters to GeoJSON format
      const points = datacenters.map(dc => ({
        type: 'Feature',
        properties: dc,
        geometry: {
          type: 'Point',
          coordinates: dc.coordinates
        }
      }))
      
      supercluster.load(points)
      return supercluster
    }

    // Generate grid lines for map overlay
    const generateGridLines = () => {
      const features = []
      const bounds = {
        minLng: 99,
        maxLng: 119,
        minLat: 1,
        maxLat: 7
      }
      const step = 0.25 // Grid spacing in degrees (approx 28km at equator)

      // Generate vertical lines (longitude)
      for (let lng = bounds.minLng; lng <= bounds.maxLng; lng += step) {
        features.push({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [lng, bounds.minLat],
              [lng, bounds.maxLat]
            ]
          }
        })
      }

      // Generate horizontal lines (latitude)
      for (let lat = bounds.minLat; lat <= bounds.maxLat; lat += step) {
        features.push({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [bounds.minLng, lat],
              [bounds.maxLng, lat]
            ]
          }
        })
      }

      return {
        type: 'FeatureCollection',
        features: features
      }
    }

    // Available Mapbox styles (verified working URLs)
    const mapStyles = {
      light: 'mapbox://styles/mapbox/light-v11',
      dark: 'mapbox://styles/mapbox/dark-v11',
      streets: 'mapbox://styles/mapbox/streets-v12',
      outdoors: 'mapbox://styles/mapbox/outdoors-v12',
      satellite: 'mapbox://styles/mapbox/satellite-v9',
      satelliteStreets: 'mapbox://styles/mapbox/satellite-streets-v12',
      // Standard is the new default style
      standard: 'mapbox://styles/mapbox/standard'
    }

    const initializeMap = async () => {
      // Load mapping libraries dynamically
      await loadMappingLibraries()
      
      // Set Mapbox access token
      mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || 'pk.your-token-here'

      // Initialize Mapbox map with style from props
      map = new mapboxgl.Map({
        container: mapContainer.value,
        style: mapStyles[props.mapStyle] || mapStyles.dark,
        center: MAP_CONFIG.defaultCenter,
        zoom: MAP_CONFIG.defaultZoom
      })


      map.on('load', async () => {
        // Add grid lines layer for sleek appearance
        map.addSource('grid-lines', {
          type: 'geojson',
          data: generateGridLines()
        })

        // Determine grid color and opacity based on map style
        const isLightStyle = props.mapStyle === 'light' || props.mapStyle === 'streets' || props.mapStyle === 'outdoors'
        const gridColor = isLightStyle ? '#000000' : '#ffffff'
        const gridOpacity = isLightStyle ? 0.12 : 0.15

        map.addLayer({
          id: 'grid-lines-layer',
          type: 'line',
          source: 'grid-lines',
          paint: {
            'line-color': gridColor,
            'line-width': 1,
            'line-opacity': gridOpacity
          }
        })

        // Initialize Deck.gl overlay
        deckOverlay = new MapboxOverlay({
          interleaved: true
        })
        map.addControl(deckOverlay)
        await updateMarkers(false) // Initial load without fitBounds - stay focused on Johor

        // Map already starts with dark theme for Tron aesthetic
      })

      // Debounced update function to prevent excessive calls
      let updateTimeout = null
      const debouncedUpdate = (eventType) => {
        if (isStyleChanging) {
          return
        }
        if (updateTimeout) {
          clearTimeout(updateTimeout)
        }
        updateTimeout = setTimeout(async () => {
          if (isStyleChanging) {
            return
          }
          await addMarkersToMap()
        }, 150) // 150ms debounce
      }

      // Update clusters when map moves or zooms
      map.on('moveend', () => debouncedUpdate('moveend'))
      map.on('zoomend', () => debouncedUpdate('zoomend'))

      // Real-time label position updates during movement (for HTML labels)
      map.on('move', updateLabelPositions)
      map.on('zoom', updateLabelPositions)
    }

    // Function to change map style
    const changeMapStyle = (styleName) => {
      if (map && mapStyles[styleName]) {
        isStyleChanging = true // Prevent debounced updates during style change
        
        // Preserve current viewport before any changes
        const currentCenter = map.getCenter()
        const currentZoom = map.getZoom()
        const currentBearing = map.getBearing()
        const currentPitch = map.getPitch()
        
        // Validate viewport coordinates before preserving
        if (!currentCenter || typeof currentCenter.lng !== 'number' || typeof currentCenter.lat !== 'number' ||
            isNaN(currentCenter.lng) || isNaN(currentCenter.lat) || 
            currentCenter.lng === 0 && currentCenter.lat === 0) {
          console.error('Invalid viewport center detected, skipping style change:', currentCenter)
          isStyleChanging = false // Reset flag
          return
        }
        
        
        // Clear existing markers first
        clearMarkers()
        
        // Properly remove existing deck overlay
        if (deckOverlay) {
          try {
            map.removeControl(deckOverlay)
            deckOverlay.finalize()
            deckOverlay = null
          } catch (error) {
            console.warn('Error cleaning up deck overlay:', error)
            deckOverlay = null
          }
        }
        
        // Reset clustering to ensure proper re-rendering
        supercluster = null
        currentMapStyle.value = styleName
        
        // Change the style
        map.setStyle(mapStyles[styleName])
        
        // Re-initialize everything after style loads
        map.once('styledata', async () => {
          try {
            
            // Wait a moment for style to fully initialize
            await new Promise(resolve => setTimeout(resolve, 100))
            
            // Restore the preserved viewport first
            map.jumpTo({
              center: currentCenter,
              zoom: currentZoom,
              bearing: currentBearing,
              pitch: currentPitch
            })
            
            // Wait for viewport to be applied
            await new Promise(resolve => setTimeout(resolve, 200))

            // Re-add grid lines after style change
            if (!map.getSource('grid-lines')) {
              map.addSource('grid-lines', {
                type: 'geojson',
                data: generateGridLines()
              })
            }

            // Update grid color based on new style
            const isLightStyle = styleName === 'light' || styleName === 'streets' || styleName === 'outdoors'
            const gridColor = isLightStyle ? '#000000' : '#ffffff'
            const gridOpacity = isLightStyle ? 0.12 : 0.15

            if (!map.getLayer('grid-lines-layer')) {
              map.addLayer({
                id: 'grid-lines-layer',
                type: 'line',
                source: 'grid-lines',
                paint: {
                  'line-color': gridColor,
                  'line-width': 1,
                  'line-opacity': gridOpacity
                }
              })
            }

            // Create new deck overlay
            deckOverlay = new MapboxOverlay({
              interleaved: true
            })

            // Add the overlay to map
            map.addControl(deckOverlay)
            
            // Wait for overlay to be fully ready before adding markers
            const isReady = await waitForOverlayReady()
            if (isReady) {
              // Double-check viewport is still correct before adding markers
              const finalCenter = map.getCenter()
              
              // Ensure we still have valid data before adding markers
              if (props.dataCenters && props.dataCenters.length > 0) {
                await addMarkersToMap()
              } else {
                console.warn('No datacenter data available after style change')
              }
              isStyleChanging = false // Reset flag when style change is complete
            } else {
              console.error('Failed to initialize overlay after style change')
              isStyleChanging = false // Reset flag on error
            }
            
          } catch (error) {
            console.error('Error reinitializing deck overlay:', error)
            isStyleChanging = false // Reset flag on error
          }
        })
      }
    }

    // Set default to dark theme for Tron aesthetic - moved to safer location

    const clearMarkers = () => {
      if (deckOverlay) {
        try {
          deckOverlay.setProps({ layers: [] })
          currentLayers = []
        } catch (error) {
          console.warn('Error clearing markers (overlay may be null):', error)
          currentLayers = []
        }
      }
      // Clear HTML cluster labels
      clearClusterLabels()
    }

    // Clear HTML cluster label elements
    const clearClusterLabels = () => {
      clusterLabelElements.forEach(el => {
        if (el && el.parentNode) {
          el.parentNode.removeChild(el)
        }
      })
      clusterLabelElements = []
    }

    // Store label data for real-time position updates
    let currentLabelData = []

    // Update HTML text labels (hybrid approach - Deck.GL for shapes, HTML for text)
    const updateClusterTextLabels = (labelData) => {
      clearClusterLabels()
      if (!map || !mapContainer.value) return

      currentLabelData = labelData

      labelData.forEach(item => {
        const point = map.project(item.labelPosition)

        // Create text label
        const label = document.createElement('div')
        label.className = 'cluster-text-label'
        label.textContent = item.count.toString()
        label.dataset.lng = item.labelPosition[0]
        label.dataset.lat = item.labelPosition[1]
        label.style.cssText = `
          position: absolute;
          left: ${point.x}px;
          top: ${point.y}px;
          transform: translate(-50%, -50%);
          color: #000000;
          font-size: 12px;
          font-weight: bold;
          font-family: Arial, sans-serif;
          pointer-events: none;
          z-index: 100;
          background: #FFFFFF;
          border: 1px solid #000000;
          padding: 4px 8px;
          min-width: 20px;
          text-align: center;
        `

        mapContainer.value.appendChild(label)
        clusterLabelElements.push(label)
      })
    }

    // Real-time update of label positions during map movement
    const updateLabelPositions = () => {
      if (!map) return
      clusterLabelElements.forEach(label => {
        const lng = parseFloat(label.dataset.lng)
        const lat = parseFloat(label.dataset.lat)
        const point = map.project([lng, lat])
        label.style.left = `${point.x}px`
        label.style.top = `${point.y}px`
      })
    }

    // HTML marker functions removed - using unified Deck.GL TextLayer approach

    // Helper function to wait for overlay readiness
    const waitForOverlayReady = async (maxAttempts = 10, delayMs = 100) => {
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (deckOverlay && map && map.hasControl(deckOverlay)) {
          try {
            deckOverlay.setProps({ layers: [] })
            return true
          } catch (error) {
          }
        }
        await new Promise(resolve => setTimeout(resolve, delayMs))
      }
      console.warn('Overlay readiness timeout after', maxAttempts, 'attempts')
      return false
    }

    const addMarkersToMap = async () => {
      if (!deckOverlay || !map) {
        return
      }
      
      // Enhanced safety check for overlay readiness
      try {
        // Check if overlay is properly attached to map
        if (!map.hasControl(deckOverlay)) {
          console.warn('Deck overlay not attached to map, skipping update')
          return
        }
        
        // Test that overlay is functional
        deckOverlay.setProps({ layers: [] })
      } catch (testError) {
        console.error('Deck overlay not ready:', testError)
        return
      }

      // Prevent concurrent updates
      if (isUpdating) {
        return
      }

      // Debounce rapid updates
      const now = Date.now()
      if (now - lastUpdateTimestamp < 200) { // 200ms debounce
        return
      }

      isUpdating = true
      lastUpdateTimestamp = now


      try {

      // Create icon atlas and mapping if not already created
      if (!iconAtlas) {
        iconAtlas = createClusterIconAtlas()
        iconMapping = createClusterIconMapping()
      }

      // Early return if no data centers provided
      if (!props.dataCenters || props.dataCenters.length === 0) {
        console.warn('No data centers provided')
        if (deckOverlay) {
          deckOverlay.setProps({ layers: [] })
        }
        return
      }

      // Filter and prepare data for clustering
      const validDataCenters = props.dataCenters
        .filter(dataCenter => {
          if (!dataCenter.coordinates || dataCenter.coordinates.length !== 2) return false
          
          const [lng, lat] = dataCenter.coordinates
          
          // Validate coordinates are valid numbers
          if (typeof lng !== 'number' || typeof lat !== 'number' || 
              isNaN(lng) || isNaN(lat)) {
            console.warn('Invalid coordinates for', dataCenter.name, [lng, lat])
            return false
          }

          // Validate coordinate ranges for Singapore/Malaysia region
          if (lng < 99 || lng > 119 || lat < 1 || lat > 7) {
            console.warn('Coordinates out of expected range for', dataCenter.name, 'lng:', lng, 'lat:', lat)
            return false
          }

          return true
        })
        .map(dataCenter => ({
          // Convert Vue Proxy to plain object
          ...JSON.parse(JSON.stringify(dataCenter)),
          coordinates: [dataCenter.coordinates[0], dataCenter.coordinates[1]]
        }))

      // Handle duplicate coordinates by slightly offsetting them
      const processedDataCenters = handleDuplicateCoordinates(validDataCenters)

      // Initialize clustering
      try {
        const cluster = initializeClustering(processedDataCenters)
        
        // Get current map bounds and zoom
        const bounds = map.getBounds()
        const zoom = Math.floor(map.getZoom())
        
        
        // Get clusters for current viewport
        const clusters = cluster.getClusters([
          bounds.getWest(),
          bounds.getSouth(), 
          bounds.getEast(),
          bounds.getNorth()
        ], zoom)

        
        // Ensure all cluster features have proper cluster properties
        const processedClusters = clusters.map(clusterFeature => {
          const isCluster = clusterFeature.properties.cluster === true
          return {
            ...clusterFeature,
            properties: {
              ...clusterFeature.properties,
              cluster: isCluster,
              // Ensure point_count exists for clusters
              point_count: isCluster ? clusterFeature.properties.point_count : 1
            }
          }
        })
        
        if (processedClusters.length === 0) {
          // At high zoom, show individual points
          const individualPoints = processedDataCenters.map(dc => ({
            type: 'Feature',
            properties: { ...dc, cluster: false, point_count: 1 },
            geometry: {
              type: 'Point',
              coordinates: dc.coordinates
            }
          }))
          processedClusters.push(...individualPoints)
        }


        // Validate data before creating layers
        if (processedClusters.length === 0) {
          console.warn('No valid clusters to display')
          deckOverlay.setProps({ layers: [] })
          return
        }

        // Validate cluster data structure
        const validClusters = processedClusters.filter(cluster => {
          if (!cluster.geometry || !cluster.geometry.coordinates) {
            console.warn('Invalid cluster geometry:', cluster)
            return false
          }
          const [lng, lat] = cluster.geometry.coordinates
          if (typeof lng !== 'number' || typeof lat !== 'number' || 
              isNaN(lng) || isNaN(lat)) {
            console.warn('Invalid cluster coordinates:', cluster)
            return false
          }
          return true
        })

        if (validClusters.length === 0) {
          console.warn('No valid clusters after validation')
          deckOverlay.setProps({ layers: [] })
          return
        }


        // Create unified ScatterplotLayer + TextLayer with offset labels (no HTML markers)
        const { ScatterplotLayer, TextLayer } = await import('@deck.gl/layers')

        let scatterLayer, textLayer, lineLayer, labelBackgroundLayer
        try {
          scatterLayer = new ScatterplotLayer({
            id: 'datacenter-clusters',
            data: validClusters,
            pickable: true,
            opacity: 0.9,
            stroked: true,
            filled: true,
            radiusScale: 1,
            radiusMinPixels: 12,
            radiusMaxPixels: 32,
            lineWidthMinPixels: 2,
            getPosition: d => {
              const coords = d.geometry?.coordinates
              if (!coords || coords.length !== 2 || 
                  typeof coords[0] !== 'number' || typeof coords[1] !== 'number' ||
                  isNaN(coords[0]) || isNaN(coords[1])) {
                console.warn('Invalid cluster coordinates:', coords, 'for:', d)
                return [103.8, 1.35] // Fallback to Singapore center
              }
              return coords
            },
            getRadius: d => {
              const isCluster = d.properties.cluster
              if (isCluster) {
                // Scale cluster size based on point count with better progression
                const count = d.properties.point_count
                if (count >= 100) return 28
                if (count >= 50) return 24  
                if (count >= 20) return 20
                if (count >= 10) return 18
                return 16 // 2-9 points
              } else {
                // Scale individual datacenter size based on power capacity
                const capacity = d.properties.power_totalmw || d.properties.capacity_mw_builtout
                if (capacity) {
                  const capacityNum = parseFloat(capacity)
                  if (capacityNum >= 100) return 22 // Very large datacenters
                  if (capacityNum >= 50) return 19  // Large datacenters
                  if (capacityNum >= 20) return 17  // Medium-large datacenters
                  if (capacityNum >= 10) return 15  // Medium datacenters
                  if (capacityNum >= 5) return 13   // Small-medium datacenters
                  return 11 // Small datacenters
                }
                return 14 // Default size for unknown capacity
              }
            },
            getFillColor: d => {
              const isCluster = d.properties.cluster
              if (isCluster) {
                // Minimalist black for clusters
                return [0, 0, 0, 255] // Black
              } else {
                // Check if this datacenter was offset due to duplicate coordinates
                if (d.properties.offsetIndex > 0) {
                  // Gray for offset datacenters
                  return [128, 128, 128, 255] // Gray
                }
                // Use colorBy prop to determine coloring method
                if (props.colorBy === 'capital') {
                  return getTronCapitalColorRGB(d.properties.ch_capital_en)
                } else {
                  // Default to status-based coloring
                  return getTronMarkerColorRGB(d.properties.status)
                }
              }
            },
            getLineColor: d => {
              const isCluster = d.properties.cluster
              if (isCluster) {
                return [255, 255, 255, 255] // White border for clusters
              } else {
                return [255, 255, 255, 255] // White border for individual points
              }
            },
            getLineWidth: d => {
              const isCluster = d.properties.cluster
              return isCluster ? 3 : 2 // Thicker border for clusters
            },
            onHover: ({ object, x, y }) => {
              
              if (object) {
                // Debug hover coordinates
                const coords = object.geometry?.coordinates
                if (coords && (coords[0] === 0 || coords[1] === 0 || isNaN(coords[0]) || isNaN(coords[1]))) {
                  console.warn('Hover detected invalid cluster coordinates:', coords, 'object:', object)
                }
                
                if (object.properties.cluster) {
                  selectedDataCenter.value = {
                    name: `${object.properties.point_count} Data Centers`,
                    status: 'cluster',
                    location: 'Clustered locations',
                    capacity: `${object.properties.point_count} facilities`
                  }
                } else {
                  selectedDataCenter.value = object.properties
                }
                tooltipStyle.value = {
                  position: 'absolute',
                  left: `${x + 10}px`,
                  top: `${y - 10}px`,
                  zIndex: 1000,
                  pointerEvents: 'none'
                }
              } else {
                selectedDataCenter.value = null
                connectorLine.value.visible = false
              }
            },
            onClick: ({ object }) => {
              if (object) {
                if (object.properties.cluster) {
                  const clusterId = object.properties.cluster_id
                  const coords = object.geometry.coordinates
                  if (coords && coords.length === 2 && 
                      typeof coords[0] === 'number' && typeof coords[1] === 'number' &&
                      !isNaN(coords[0]) && !isNaN(coords[1]) &&
                      coords[0] !== 0 && coords[1] !== 0) {
                    const expansionZoom = cluster.getClusterExpansionZoom(clusterId)
                    map.flyTo({
                      center: coords,
                      zoom: expansionZoom,
                      duration: 1000
                    })
                  } else {
                    console.warn('Invalid coordinates for cluster flyTo:', coords)
                  }
                } else {
                  const coords = object.geometry.coordinates
                  if (coords && coords.length === 2 && 
                      typeof coords[0] === 'number' && typeof coords[1] === 'number' &&
                      !isNaN(coords[0]) && !isNaN(coords[1]) &&
                      coords[0] !== 0 && coords[1] !== 0) {
                    map.flyTo({
                      center: coords,
                      zoom: 12,
                      duration: 1000
                    })
                    // Clear hover tooltip when opening click dialog
                    selectedDataCenter.value = null
                connectorLine.value.visible = false
                    emit('data-center-select', object.properties)
                  } else {
                    console.warn('Invalid coordinates for individual datacenter flyTo:', coords)
                  }
                }
              }
            }
          })

          // Get clusters for labels
          const clusterData = validClusters.filter(d => d.properties.cluster && d.properties.point_count >= 2)

          // Calculate zoom-responsive sizes (shorter lines)
          const currentZoom = map.getZoom()
          const getPixelOffset = (zoom) => {
            if (zoom <= 6) return 35
            if (zoom <= 8) return 30
            if (zoom <= 10) return 28
            if (zoom <= 12) return 25
            return 22
          }
          const pixelOffset = getPixelOffset(currentZoom)

          // Import PathLayer for lines
          const { PathLayer } = await import('@deck.gl/layers')

          // Create label data with offset positions (in pixels, converted to geo coords)
          const labelData = clusterData.map(cluster => {
            const coords = cluster.geometry.coordinates
            const count = cluster.properties.point_count
            // Calculate pixel offset in geographic coordinates
            // At the equator, 1 degree ≈ 111km, so offset scales with zoom
            const metersPerPixel = 40075016.686 * Math.cos(coords[1] * Math.PI / 180) / Math.pow(2, currentZoom + 8)
            const offsetMeters = pixelOffset * metersPerPixel
            const offsetDegrees = offsetMeters / 111000 // approximate

            // Calculate circle radius in degrees (to offset line start to edge)
            // Use smaller values to match visual circle size
            const circleRadiusPixels = count >= 100 ? 6 : count >= 50 ? 5 : count >= 20 ? 4 : count >= 10 ? 4 : 3
            const circleRadiusMeters = circleRadiusPixels * metersPerPixel
            const circleRadiusDegrees = circleRadiusMeters / 111000

            // Direction vector from center to label (normalized)
            const dx = 1.2 // x direction component
            const dy = 1.0 // y direction component
            const magnitude = Math.sqrt(dx * dx + dy * dy)
            const normX = dx / magnitude
            const normY = dy / magnitude

            // Line start at edge of circle
            const lineStart = [
              coords[0] + circleRadiusDegrees * normX,
              coords[1] + circleRadiusDegrees * normY
            ]

            return {
              position: coords,
              lineStart: lineStart,
              labelPosition: [coords[0] + offsetDegrees * 1.2, coords[1] + offsetDegrees],
              count: count,
              text: count.toString()
            }
          })

          // PathLayer for connecting lines (minimalist light gray, starts at circle edge)
          lineLayer = new PathLayer({
            id: 'cluster-label-lines',
            data: labelData,
            getPath: d => [d.lineStart, d.labelPosition],
            getColor: [160, 160, 160, 255], // Light gray
            getWidth: 1,
            widthUnits: 'pixels',
            widthMinPixels: 1,
            widthMaxPixels: 2,
            pickable: false
          })

          // No ScatterplotLayer - using HTML for square boxes
          labelBackgroundLayer = null

          // Use HTML for text labels (hybrid approach - Deck.GL TextLayer doesn't render on all map themes)
          textLayer = null

          // Create HTML text labels on top of Deck.GL circles
          updateClusterTextLabels(labelData)
        } catch (error) {
          console.error('Failed to create layers:', error)
          // Use fallback layer without clustering
          deckOverlay.setProps({ layers: [] })
          return
        }
        
        // Clear existing layers first to prevent conflicts
        if (currentLayers.length > 0 && deckOverlay) {
          try {
            deckOverlay.setProps({ layers: [] })
            await new Promise(resolve => setTimeout(resolve, 50)) // Small delay
          } catch (clearError) {
            console.error('Failed to clear layers:', clearError)
            // Reset overlay state
            currentLayers = []
          }
        }
        
        // Set new layers (ScatterplotLayer + TextLayer with background)
        try {
          if (!deckOverlay) {
            console.error('deckOverlay is null when trying to set layers')
            return
          }
          // Filter out null layers
          const activeLayers = [lineLayer, scatterLayer, labelBackgroundLayer, textLayer].filter(l => l !== null)
          deckOverlay.setProps({
            layers: activeLayers
          })
          currentLayers = activeLayers
        } catch (layerError) {
          console.error('Failed to set layers:', layerError)
          // Clear layers and try again with empty state
          if (deckOverlay) {
            try {
              deckOverlay.setProps({ layers: [] })
            } catch (clearError) {
              console.error('Failed to clear layers after error:', clearError)
            }
          }
          currentLayers = []
          return
        }
        
        
      } catch (error) {
        console.error('Clustering failed, using fallback ScatterplotLayer:', error)
        
        // Fallback to ScatterplotLayer if clustering fails
        const { ScatterplotLayer } = await import('@deck.gl/layers')
        const fallbackLayer = new ScatterplotLayer({
          id: 'datacenter-fallback',
          data: processedDataCenters,
          pickable: true,
          opacity: 0.8,
          stroked: true,
          filled: true,
          radiusScale: 1,
          radiusMinPixels: 12,
          radiusMaxPixels: 20,  
          lineWidthMinPixels: 2,
          getPosition: d => {
            const coords = d.coordinates
            if (!coords || coords.length !== 2 || 
                typeof coords[0] !== 'number' || typeof coords[1] !== 'number' ||
                isNaN(coords[0]) || isNaN(coords[1])) {
              console.warn('Invalid datacenter coordinates:', coords, 'for:', d)
              return [103.8, 1.35] // Fallback to Singapore center
            }
            return coords
          },
          getRadius: d => {
            // Scale datacenter size based on power capacity (fallback layer)
            const capacity = d.power_totalmw || d.capacity_mw_builtout
            if (capacity) {
              const capacityNum = parseFloat(capacity)
              if (capacityNum >= 100) return 22 // Very large datacenters
              if (capacityNum >= 50) return 19  // Large datacenters
              if (capacityNum >= 20) return 17  // Medium-large datacenters
              if (capacityNum >= 10) return 15  // Medium datacenters
              if (capacityNum >= 5) return 13   // Small-medium datacenters
              return 11 // Small datacenters
            }
            return 14 // Default size for unknown capacity
          },
          getFillColor: d => {
            // Use colorBy prop to determine coloring method for fallback layer
            if (props.colorBy === 'capital') {
              return getCapitalColorRGB(d.ch_capital_en)
            } else {
              return getMarkerColorRGB(d.status)
            }
          },
          getLineColor: [255, 255, 255, 200],
          onHover: ({ object, x, y }) => {
            if (object) {
              // Debug hover coordinates
              const coords = object.coordinates
              if (coords && (coords[0] === 0 || coords[1] === 0 || isNaN(coords[0]) || isNaN(coords[1]))) {
                console.warn('Hover detected invalid datacenter coordinates:', coords, 'object:', object)
              }
              
              selectedDataCenter.value = object
              tooltipStyle.value = {
                position: 'absolute',
                left: `${x + 10}px`,
                top: `${y - 10}px`,
                zIndex: 1000,
                pointerEvents: 'none'
              }
            } else {
              selectedDataCenter.value = null
            }
          },
          onClick: ({ object }) => {
            if (object) {
              map.flyTo({
                center: object.coordinates,
                zoom: 12,
                duration: 1000
              })
              // Clear hover tooltip when opening click dialog
              selectedDataCenter.value = null
              emit('data-center-select', object)
            }
          }
        })
        
        // Clear existing layers first to prevent conflicts
        if (currentLayers.length > 0) {
          deckOverlay.setProps({ layers: [] })
          await new Promise(resolve => setTimeout(resolve, 50)) // Small delay
        }
        
        if (deckOverlay) {
          try {
            deckOverlay.setProps({
              layers: [fallbackLayer]
            })
          } catch (error) {
            console.error('Failed to set fallback layer:', error)
            return
          }
        } else {
          console.error('deckOverlay is null when trying to set fallback layer')
          return
        }
        currentLayers = [fallbackLayer]
        
        // No HTML markers needed for fallback - pure Deck.GL approach
        
      }
      } finally {
        isUpdating = false
      }
    }


    const updateMarkers = async (shouldFitBounds = false) => {
      if (!map) return
      
      clearMarkers()

      if (shouldFitBounds) {
        // Fit bounds to show all datacenters if we have data (initial load only)
        if (props.dataCenters.length > 0) {
          const bounds = new mapboxgl.LngLatBounds()
          let validCoordinates = 0
          
          props.dataCenters.forEach(dataCenter => {
            if (dataCenter.coordinates && dataCenter.coordinates.length === 2) {
              // Convert Vue Proxy coordinates to plain array
              const coordinates = [dataCenter.coordinates[0], dataCenter.coordinates[1]]
              const [lng, lat] = coordinates
              if (typeof lng === 'number' && typeof lat === 'number' && 
                  !isNaN(lng) && !isNaN(lat) &&
                  lng >= 99 && lng <= 119 && lat >= 1 && lat <= 7) {
                bounds.extend(coordinates)
                validCoordinates++
              }
            }
          })
          
          // Fit the map to show all markers with some padding, then add markers
          if (validCoordinates > 0) {
            map.fitBounds(bounds, {
              padding: 50,
              maxZoom: 8 // Lower maxZoom to show better cluster detail
            })
            
            // Add markers after the map bounds are set
            map.once('moveend', async () => {
              await addMarkersToMap()
            })
          } else {
            // If no valid coordinates for bounds, just add markers
            await addMarkersToMap()
          }
        } else {
          await addMarkersToMap()
        }
      } else {
        // Skip fitBounds and directly add markers (preserves current viewport)
        await addMarkersToMap()
      }
    }

    // Watch for data changes
    watch(() => props.dataCenters, async () => {
      await updateMarkers(false) // Don't fit bounds on data updates
    }, { deep: true })

    onMounted(async () => {
      await initializeMap()
    })

    onUnmounted(() => {
      clearMarkers()
      clearClusterLabels()

      // Properly clean up deck overlay
      if (deckOverlay && map) {
        try {
          map.removeControl(deckOverlay)
          deckOverlay.finalize()
        } catch (error) {
          console.warn('Error cleaning up deck overlay on unmount:', error)
        }
        deckOverlay = null
      }

      if (map) {
        map.remove()
      }
    })

    // Format style names for UI
    const formattedStyles = Object.keys(mapStyles).map(key => ({
      title: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
      value: key
    }))

    // Formatting functions for tooltip data
    const formatPowerCapacity = (dc) => {
      // Priority: Use estimated_capacity first if available, then power_totalmw, then capacity_mw_builtout
      const capacity = dc.estimated_capacity || dc.power_totalmw || dc.capacity_mw_builtout;
      if (capacity) {
        const capacityNum = parseFloat(capacity);
        if (!isNaN(capacityNum)) {
          const suffix = dc.estimated_capacity ? ' MW*' : ' MW';
          return `${capacityNum.toFixed(1)}${suffix}`;
        }
      }
      return 'N/A'
    }

    const formatBuildingArea = (dc) => {
      // Use building_area_unit_sqm column directly
      if (dc.building_area_unit_sqm) return `${dc.building_area_unit_sqm.toLocaleString()} m²`
      return 'N/A'
    }

    const formatISOCertification = (dc) => {
      if (dc.standards_iso27001 === '1' || dc.standards_iso27001 === '1.0') return 'Certified'
      if (dc.standards_iso27001 === '0' || dc.standards_iso27001 === '0.0') return 'Not Certified'
      return 'N/A'
    }

    const formatSecurityFeatures = (dc) => {
      const features = []
      if (dc.security_cctv === 'True' || dc.security_cctv === '1') features.push('CCTV')
      if (dc.security_guards && dc.security_guards !== '') features.push('Guards')
      if (dc.security_biometric === 'True' || dc.security_biometric === '1') features.push('Biometric')
      if (dc.security_keycard === 'True' || dc.security_keycard === '1') features.push('Keycard')
      return features.length > 0 ? features.join(', ') : 'N/A'
    }

    const formatPUE = (dc) => {
      // Handle various PUE value formats and ensure proper display
      const pue = dc.power_pue
      if (pue === null || pue === undefined || pue === '' || pue === 0) {
        return 'N/A'
      }
      
      // Convert to number and validate
      const pueNum = parseFloat(pue)
      if (isNaN(pueNum)) {
        return 'N/A'
      }
      
      // Format PUE with appropriate decimal places
      if (pueNum >= 1) {
        return pueNum.toFixed(1)
      } else {
        return pueNum.toFixed(2)
      }
    }

    const formatFacilityAge = (dc) => {
      if (dc && dc.facility_age && !isNaN(dc.facility_age)) {
        const age = parseInt(dc.facility_age)
        if (age === 0) return 'New'
        if (age === 1) return '1 year'
        return `${age} years`
      }

      // Fallback to calculating from building_year_operational
      if (dc && dc.building_year_operational && !isNaN(dc.building_year_operational)) {
        const currentYear = new Date().getFullYear()
        const age = currentYear - parseInt(dc.building_year_operational)
        if (age === 0) return 'New'
        if (age === 1) return '1 year'
        return `${age} years`
      }

      return 'N/A'
    }

    const formatCapitalInvestment = (dc) => {
      // Display capital ownership type (English translation)
      if (dc && dc.ch_capital_en && dc.ch_capital_en !== '') {
        return dc.ch_capital_en
      }

      // Fallback to Chinese if English not available
      if (dc && dc.ch_capital && dc.ch_capital !== '') {
        return dc.ch_capital
      }

      return 'N/A'
    }


    return {
      mapContainer,
      selectedDataCenter,
      tooltipStyle,
      currentMapStyle,
      connectorLine,
      getStatusColor,
      formatPowerCapacity,
      formatBuildingArea,
      formatFacilityAge,
      formatISOCertification,
      formatSecurityFeatures,
      formatPUE,
      formatCapitalInvestment,
      getCapitalCategory,
      flyToLocation,
      zoomIn,
      zoomOut,
      resetView
    }
  }
}
</script>

<style scoped>
/* Minimalist black and white map */
.map-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 600px;
  background: #ffffff;
  overflow: hidden;
}

/* Remove all animations and grid overlays for minimalist design */


.map {
  width: 100%;
  height: 100%;
}

/* Minimalist tooltip styling */
.tooltip-card {
  max-width: 300px;
  pointer-events: none !important;
  z-index: 1000;
  background: #ffffff !important;
  border: 1px solid #000000 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
  border-radius: 0 !important;
}

.tooltip-card :deep(.v-card-title) {
  color: #000000 !important;
  font-weight: 600 !important;
  font-size: 1rem !important;
}

.tooltip-card :deep(.v-card-text) {
  color: #000000 !important;
  font-size: 0.875rem !important;
}

.tooltip-card :deep(.v-card-text strong) {
  color: #000000 !important;
  font-weight: 500 !important;
}

/*
 * Zoom controls - top right corner (below status bar)
 * Position values:
 *   top: 80px = status bar height (~49px) + 31px margin
 *   right: 48px = offset from edge to avoid overlap with other UI elements
 */
.zoom-controls {
  position: absolute;
  top: 80px;
  right: 48px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 100;
}

/* Zoom button base styles - using :deep() for Vuetify overrides */
.zoom-controls .zoom-btn.v-btn {
  background-color: #ffffff;
  color: #000000;
  border: 1px solid #000000;
  border-radius: 0;
  width: 32px;
  height: 32px;
  min-width: 32px;
  box-shadow: none;
}

.zoom-controls .zoom-btn.v-btn:hover {
  background-color: #000000;
  color: #ffffff;
}

.zoom-controls .zoom-btn :deep(.v-btn__content) {
  color: inherit;
}

.zoom-controls .zoom-btn :deep(.v-icon) {
  font-size: 18px;
  color: inherit;
}
</style>
